import { API_SECRET, APP_VERSION, LIVE_ACTIVITY_DEVICE_URL, MAPS_PLATFORM } from "../config/appConfig";
import { getRemoteLiveActivityTokenStatus, persistBackendCredentials, requestRemoteLiveActivityAuthorization, startRemoteLiveActivityTokenUpdates } from "./liveActivities";
import { TREMBO_INTERNAL_API_KEY } from "../config/appConfig";
import { traceAndFetch } from "../sync/apiTrace"; // v3.2.08

function normalizeText(value, fallback = "") {
  const text = typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
  return text || fallback;
}

function maskToken(token = "") {
  const raw = normalizeText(token);
  if (!raw) return "";
  if (raw.length <= 14) return `${raw.slice(0, 4)}…${raw.slice(-4)}`;
  return `${raw.slice(0, 8)}…${raw.slice(-6)}`;
}

const REMOTE_LIVE_ACTIVITY_NATIVE_STATUS_TTL_MS = 10 * 60 * 1000;
const REMOTE_LIVE_ACTIVITY_CREDENTIALS_TTL_MS = 10 * 60 * 1000;
let remoteLiveActivityTokenUpdatesStarted = false;
let remoteLiveActivityTokenStatusCache = null;
let remoteLiveActivityTokenStatusCacheAt = 0;
let remoteLiveActivityStatusInFlight = null;
let remoteLiveActivityCredentialsFingerprint = "";
let remoteLiveActivityCredentialsPersistedAt = 0;

function shouldUseRemoteLiveActivityStatusCache(forceRefresh = false) {
  return !forceRefresh
    && remoteLiveActivityTokenStatusCache
    && remoteLiveActivityTokenStatusCacheAt > 0
    && Date.now() - remoteLiveActivityTokenStatusCacheAt < REMOTE_LIVE_ACTIVITY_NATIVE_STATUS_TTL_MS;
}

async function persistBackendCredentialsSafely(payload = {}, { force = false } = {}) {
  const fingerprint = JSON.stringify({
    liveActivityDeviceUrl: normalizeText(payload.liveActivityDeviceUrl || ""),
    apiKey: normalizeText(payload.apiKey || ""),
    activeFarmId: normalizeText(payload.activeFarmId || ""),
    accountId: normalizeText(payload.accountId || ""),
    deviceId: normalizeText(payload.deviceId || ""),
    username: normalizeText(payload.username || ""),
  });
  const now = Date.now();
  if (!force && fingerprint === remoteLiveActivityCredentialsFingerprint && remoteLiveActivityCredentialsPersistedAt > 0 && now - remoteLiveActivityCredentialsPersistedAt < REMOTE_LIVE_ACTIVITY_CREDENTIALS_TTL_MS) {
    return { ok: true, skipped: true, reason: "credentials_persist_throttled" };
  }
  remoteLiveActivityCredentialsFingerprint = fingerprint;
  remoteLiveActivityCredentialsPersistedAt = now;
  return persistBackendCredentials(payload);
}


export function summarizeRemoteLiveActivityTokenStatus(status = null) {
  const data = status && typeof status === "object" ? status : {};
  const token = normalizeText(data.pushToStartToken || data.push_to_start_token || "");
  return {
    supported: data.supported !== false,
    bridgeReady: data.bridgeReady === true,
    remotePushSupported: data.remotePushSupported === true,
    activitiesEnabled: data.activitiesEnabled === true,
    widgetExtensionReady: data.widgetExtensionReady === true,
    tokenAvailable: data.tokenAvailable === true || !!token,
    tokenMasked: maskToken(token),
    tokenLength: Number(data.tokenLength || token.length || 0),
    tokenUpdatedAt: normalizeText(data.tokenUpdatedAt || data.token_updated_at || ""),
    remoteNotificationDeviceTokenAvailable: data.remoteNotificationDeviceTokenAvailable === true || !!normalizeText(data.remoteNotificationDeviceToken || data.remote_notification_device_token || ""),
    remoteNotificationDeviceToken: normalizeText(data.remoteNotificationDeviceToken || data.remote_notification_device_token || ""),
    remoteNotificationDeviceTokenLength: Number(data.remoteNotificationDeviceTokenLength || data.remote_notification_device_token_length || 0),
    remoteNotificationDeviceTokenUpdatedAt: normalizeText(data.remoteNotificationDeviceTokenUpdatedAt || data.remote_notification_device_token_updated_at || ""),
    activityPushTokenAvailable: data.activityPushTokenAvailable === true || !!normalizeText(data.activityPushToken || data.activity_push_token || ""),
    activityPushTokenMasked: maskToken(data.activityPushToken || data.activity_push_token || ""),
    activityPushTokenCount: Number(data.activityPushTokenCount || data.activity_push_token_count || 0),
    activityPushTokens: data.activityPushTokens || data.activity_push_tokens || {},
    apnsEnvironment: normalizeText(data.apnsEnvironment || data.apns_environment || data.environment || "unknown"),
    authorizationStatus: normalizeText(data.authorizationStatus || data.authorization_status || "unknown"),
    reason: normalizeText(data.reason || ""),
    error: normalizeText(data.error || ""),
  };
}

export async function ensureRemoteLiveActivityTokenStatus({ requestAuthorization = false, forceRefresh = false } = {}) {
  if (shouldUseRemoteLiveActivityStatusCache(forceRefresh || requestAuthorization)) {
    return remoteLiveActivityTokenStatusCache;
  }
  if (remoteLiveActivityStatusInFlight) return remoteLiveActivityStatusInFlight;

  remoteLiveActivityStatusInFlight = (async () => {
    if (requestAuthorization) {
      try { await requestRemoteLiveActivityAuthorization({ source: "remote-token-registration" }); } catch (_error) {}
    }
    let status = await getRemoteLiveActivityTokenStatus({ source: "remote-token-registration" });
    if (status?.supported !== false && status?.remotePushSupported !== false && !remoteLiveActivityTokenUpdatesStarted) {
      remoteLiveActivityTokenUpdatesStarted = true;
      const started = await startRemoteLiveActivityTokenUpdates({ source: "remote-token-registration" });
      status = started || status;
    }
    remoteLiveActivityTokenStatusCache = status;
    remoteLiveActivityTokenStatusCacheAt = Date.now();
    return status;
  })();

  try {
    return await remoteLiveActivityStatusInFlight;
  } finally {
    remoteLiveActivityStatusInFlight = null;
  }
}

const REMOTE_LIVE_ACTIVITY_MULTI_FARM_THROTTLE_MS = 25 * 60 * 1000;
const remoteLiveActivityFarmBatchCache = new Map();

// v2.220.00: fingerprint condicional de registro de device.
// Só posta ao backend quando algo material mudou: token, versão, usuário, fazenda.
// TTL de 4h para re-registro periódico de presença (keepalive).
const REMOTE_LIVE_ACTIVITY_DEVICE_REG_TTL_MS = 4 * 60 * 60 * 1000;
const DEVICE_REG_FINGERPRINT_KEY = "fz:liveActivity:deviceRegFP:v1";

function readDeviceRegFingerprintMap() {
  try {
    const raw = window?.localStorage?.getItem(DEVICE_REG_FINGERPRINT_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_) { return {}; }
}

function writeDeviceRegFingerprintMap(map) {
  try {
    const entries = Object.entries(map || {}).slice(-40);
    window?.localStorage?.setItem(DEVICE_REG_FINGERPRINT_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch (_) {}
}

function buildDeviceRegFingerprint(farmId, summary, session, appVersion) {
  return JSON.stringify({
    farmId: normalizeText(farmId),
    pushToken: normalizeText(summary?.tokenMasked || ""),
    activityToken: normalizeText(summary?.activityPushTokenMasked || ""),
    apnsEnv: normalizeText(summary?.apnsEnvironment || ""),
    appVersion: normalizeText(appVersion || ""),
    accountId: normalizeText(session?.id || session?.accountId || ""),
    username: normalizeText(session?.username || ""),
  });
}

function deviceNeedsRegistration(farmId, summary, session, appVersion) {
  try {
    const map = readDeviceRegFingerprintMap();
    const key = `${normalizeText(farmId)}:${normalizeText(session?.id || session?.username || "")}`;
    const entry = map[key];
    if (!entry) return true;
    if (Date.now() - (entry.at || 0) > REMOTE_LIVE_ACTIVITY_DEVICE_REG_TTL_MS) return true;
    return entry.fp !== buildDeviceRegFingerprint(farmId, summary, session, appVersion);
  } catch (_) { return true; }
}

function markDeviceRegistered(farmId, summary, session, appVersion) {
  try {
    const map = readDeviceRegFingerprintMap();
    const key = `${normalizeText(farmId)}:${normalizeText(session?.id || session?.username || "")}`;
    map[key] = { fp: buildDeviceRegFingerprint(farmId, summary, session, appVersion), at: Date.now() };
    writeDeviceRegFingerprintMap(map);
  } catch (_) {}
}

function isAdminOrGestorSession(session = {}) {
  const role = normalizeText(session?.role || session?.userRole || session?.profile || "").toLowerCase();
  return ["admin", "administrador", "gestor", "manager"].includes(role);
}

function isFarmActiveForRemoteLiveActivity(farm = {}) {
  if (!farm || typeof farm !== "object") return false;
  if (farm._deleted || farm.deletedAt || farm.deleted_at) return false;
  const status = normalizeText(farm.status || farm.farmStatus || farm.state || "").toLowerCase();
  if (["inativa", "inativo", "inactive", "disabled", "desativada", "desativado"].includes(status)) return false;
  if (farm.isActive === false || farm.active === false || farm.enabled === false) return false;
  return !!normalizeText(farm.id || farm.farm_id || farm.farmId || "");
}

function farmIdFromFarm(farm = {}) {
  return normalizeText(farm?.id || farm?.farm_id || farm?.farmId || "");
}

function sessionKeyForRemoteLiveActivity(session = {}, deviceId = "") {
  return [
    normalizeText(session?.id || session?.accountId || session?.account_id || session?.username || session?.name || "unknown"),
    normalizeText(deviceId, "unknown"),
  ].join(":");
}

function shouldThrottleRemoteLiveActivityFarmBatch(session, deviceId, farms, forceAll = false) {
  if (forceAll) return false;
  const key = sessionKeyForRemoteLiveActivity(session, deviceId);
  const farmIds = (Array.isArray(farms) ? farms : []).map(farmIdFromFarm).filter(Boolean).sort().join(",");
  const cacheKey = `${key}:${farmIds}`;
  const now = Date.now();
  const last = remoteLiveActivityFarmBatchCache.get(cacheKey) || 0;
  if (last && now - last < REMOTE_LIVE_ACTIVITY_MULTI_FARM_THROTTLE_MS) return true;
  remoteLiveActivityFarmBatchCache.set(cacheKey, now);
  return false;
}

async function postRemoteLiveActivityDeviceRegistration({ farm, session, deviceId, status, summary } = {}) {
  const farmId = farmIdFromFarm(farm);
  const accountId = normalizeText(session?.id || session?.accountId || session?.account_id || "");
  const username = normalizeText(session?.username || "");
  const token = normalizeText(status?.pushToStartToken || status?.push_to_start_token || "");
  if (!farmId || (!accountId && !username)) {
    return { registered: false, skipped: true, reason: "missing_identity", farmId };
  }
  // v2.220.00: só posta se algo material mudou (token, versão, usuário, fazenda, TTL 4h).
  if (!deviceNeedsRegistration(farmId, summary, session, APP_VERSION)) {
    return { registered: false, skipped: true, reason: "device_registration_unchanged", farmId };
  }
  const response = await traceAndFetch(LIVE_ACTIVITY_DEVICE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": API_SECRET,
      "X-Device-ID": normalizeText(deviceId, "unknown"),
      "X-User-Name": session?.name || session?.username || "",
    },
    body: JSON.stringify({
      farm_id: farmId,
      account_id: accountId,
      user_id: normalizeText(session?.userId || session?.user_id || ""),
      username,
      device_id: normalizeText(deviceId, "unknown"),
      platform: MAPS_PLATFORM || "ios",
      app_version: APP_VERSION,
      bundle_id: "br.com.fazendalagoazul.trembo",
      environment: summary.apnsEnvironment,
      apns_environment: summary.apnsEnvironment,
      authorization_status: summary.authorizationStatus,
      token_status: summary.reason || (token ? "token_available" : "token_pending"),
      push_to_start_token: token,
      remote_notification_device_token: summary.remoteNotificationDeviceToken,
      background_push_token: summary.remoteNotificationDeviceToken,
      activity_push_token: normalizeText(status?.activityPushToken || status?.activity_push_token || ""),
      activity_push_tokens: summary.activityPushTokens,
      token_updated_at: summary.tokenUpdatedAt,
      is_active: 1,
    }),
  }, { source: "live-activity-device.register", action: "register" });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.ok === false) {
    return { registered: false, farmId, error: json?.error || `HTTP ${response.status}`, status: summary };
  }
  markDeviceRegistered(farmId, summary, session, APP_VERSION);
  return { registered: true, farmId, remote: json?.data || json, status: summary };
}

export async function registerRemoteLiveActivityDevice({ farm, session, deviceId, requestAuthorization = false } = {}) {
  const farmId = farmIdFromFarm(farm);
  const accountId = normalizeText(session?.id || session?.accountId || session?.account_id || "");
  const username = normalizeText(session?.username || "");
  if (!farmId || (!accountId && !username)) {
    return { registered: false, skipped: true, reason: "missing_identity" };
  }

  const status = await ensureRemoteLiveActivityTokenStatus({ requestAuthorization });
  const summary = summarizeRemoteLiveActivityTokenStatus(status);
  if (!status?.supported || !status?.bridgeReady) {
    return { registered: false, skipped: true, reason: status?.reason || "native_bridge_unavailable", status: summary };
  }

  return postRemoteLiveActivityDeviceRegistration({ farm, session, deviceId, status, summary });
}

export async function registerRemoteLiveActivityDeviceForFarms({ farms, activeFarmId = "", session, deviceId, requestAuthorization = false, forceAll = false } = {}) {
  const safeFarms = (Array.isArray(farms) ? farms : []).filter(isFarmActiveForRemoteLiveActivity);
  if (!safeFarms.length) return { registered: false, skipped: true, reason: "no_active_farms", results: [] };

  const isAdminOrGestor = isAdminOrGestorSession(session);
  const selectedFarms = isAdminOrGestor
    ? safeFarms
    : [safeFarms.find((farm) => farmIdFromFarm(farm) === normalizeText(activeFarmId)) || safeFarms[0]].filter(Boolean);

  if (!selectedFarms.length) return { registered: false, skipped: true, reason: "no_target_farms", results: [] };

  let farmsToRegister = selectedFarms;
  let throttled = false;
  if (isAdminOrGestor && shouldThrottleRemoteLiveActivityFarmBatch(session, deviceId, selectedFarms, forceAll)) {
    throttled = true;
    // Não refaz o batch completo dentro do throttle, mas mantém a fazenda ativa atualizada.
    // Isso preserva last_seen_at/activity_push_tokens_json após uma Live remota gerar token async.
    const activeFarm = safeFarms.find((farm) => farmIdFromFarm(farm) === normalizeText(activeFarmId)) || selectedFarms[0];
    farmsToRegister = activeFarm ? [activeFarm] : [];
  }
  if (!farmsToRegister.length) {
    return { registered: false, skipped: true, throttled, reason: throttled ? "multi_farm_registration_throttled_no_active_farm" : "no_target_farms", farmCount: selectedFarms.length, results: [] };
  }

  try {
    await persistBackendCredentialsSafely({
      liveActivityDeviceUrl: LIVE_ACTIVITY_DEVICE_URL,
      apiKey: TREMBO_INTERNAL_API_KEY,
      activeFarmId: activeFarmId || farmIdFromFarm(farmsToRegister[0]) || "",
      accountId: normalizeText(session?.id || session?.accountId || session?.account_id || ""),
      deviceId: normalizeText(deviceId, "unknown"),
      username: normalizeText(session?.username || ""),
    }, { force: !!forceAll });
  } catch (_error) {}

  const status = await ensureRemoteLiveActivityTokenStatus({ requestAuthorization, forceRefresh: !!forceAll });
  const summary = summarizeRemoteLiveActivityTokenStatus(status);
  if (!status?.supported || !status?.bridgeReady) {
    return { registered: false, skipped: true, reason: status?.reason || "native_bridge_unavailable", status: summary, results: [] };
  }

  const results = [];
  for (const farm of farmsToRegister) {
    try {
      results.push(await postRemoteLiveActivityDeviceRegistration({ farm, session, deviceId, status, summary }));
    } catch (error) {
      results.push({ registered: false, farmId: farmIdFromFarm(farm), error: error?.message || String(error), status: summary });
    }
  }

  const registeredCount = results.filter((item) => item?.registered).length;
  return {
    registered: registeredCount > 0,
    registeredCount,
    farmCount: selectedFarms.length,
    registeredFarmCount: farmsToRegister.length,
    adminScope: isAdminOrGestor,
    throttled,
    status: summary,
    results,
  };
}

